"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { Loader, CircleCheck, ArrowRight, Calendar, Building2, User, Mail, Phone, MessageSquare } from "lucide-react";
export default function BookingRequest() {
  return <section id="booking-request" className="dark bg-background text-foreground relative min-h-screen py-24 overflow-hidden flex items-center justify-center">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4">
            Reserve Your <span className="text-primary">Demo</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See exactly how our branded campaigns and aged lead systems can scale your home improvement business. Fill out the details below to secure your spot.
          </p>
        </div>

        <FormProvider formId="booking_request_reserve_your_demo" sectionName="Booking Request" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        company: "",
        project_type: "",
        preferred_date: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        company: {
          required: true
        },
        project_type: {
          required: true
        },
        preferred_date: {
          required: true
        },
        message: {}
      }} submitText="Submit Request" successMessage="Demo request received! We will contact you shortly to confirm your time.">
          <BookingFormInner />
        </FormProvider>
      </div>
    </section>;
}
function BookingFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      setProgress(100);
    } else {
      setProgress(15);
    }
  }, [isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-none p-12 text-center shadow-2xl animate-in fade-in zoom-in duration-500 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-linear-to-r from-primary to-chart-4" />
        <div className="w-20 h-20 bg-primary/20 rounded-none flex items-center justify-center mx-auto mb-6">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-4 text-foreground">Request Confirmed</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] rounded-none border-accent/40 hover:bg-accent/10">
          Book Another Demo
        </Button>
      </div>;
  }
  return <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-none shadow-2xl overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-linear-to-r from-primary to-chart-4" />
      <div className="p-6 md:p-10">
        <div className="mb-8">
          <div className="flex justify-between text-sm font-medium text-muted-foreground mb-2">
            <span>Form Completion</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-1 rounded-none" />
        </div>

        <Form onSubmit={handleSubmit} className="space-y-10">
          {}
          <div className="space-y-6">
            <div className="flex items-center gap-3 border-b border-border/50 pb-4">
              <div className="bg-primary/20 p-2 rounded-none">
                <User className="size-5 text-primary" />
              </div>
              <h3 className="text-xl font-semibold uppercase tracking-wider text-sm text-muted-foreground">1. Your Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="full_name" rules={{
              required: "Full name is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                        <Input placeholder="John Doe" className="pl-10 rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent!" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(Math.max(progress, 30));
                  }} />
                      </div>
                    </FormControl>
                    <FormDescription>Enter the name of the person attending the demo.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="company" rules={{
              required: "Company name is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Company / Organization</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                        <Input placeholder="Acme Home Improvements" className="pl-10 rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent!" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(Math.max(progress, 45));
                  }} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                        <Input type="email" placeholder="john@example.com" className="pl-10 rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent!" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(Math.max(progress, 60));
                  }} />
                      </div>
                    </FormControl>
                    <FormDescription>We'll send the demo confirmation to this address.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                        <Input type="tel" placeholder="+1 (555) 123-4567" className="pl-10 rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent!" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(Math.max(progress, 75));
                  }} />
                      </div>
                    </FormControl>
                    <FormDescription>Best number to reach you for any changes.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {}
          <div className="space-y-6 pt-4">
            <div className="flex items-center gap-3 border-b border-border/50 pb-4">
              <div className="bg-primary/20 p-2 rounded-none">
                <SafeHydrate><Calendar className="size-5 text-primary" /></SafeHydrate>
              </div>
              <h3 className="text-xl font-semibold uppercase tracking-wider text-sm text-muted-foreground">2. Project Needs</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="project_type" rules={{
              required: "Please select a project type"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Project Type</FormLabel>
                    <Select onValueChange={val => {
                field.onChange(val);
                setProgress(Math.max(progress, 85));
              }} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent! w-full">
                          <SelectValue placeholder="Select a project type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Kitchen Renovation">Kitchen Renovation</SelectItem>
                        <SelectItem value="Bathroom Remodel">Bathroom Remodel</SelectItem>
                        <SelectItem value="Solar Installation">Solar Installation</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="preferred_date" rules={{
              required: "Please select a preferred date"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Preferred Demo Date</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input type="date" className="pl-3 rounded-none border-0 border-b border-accent/40 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 bg-transparent!" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(Math.max(progress, 95));
                  }} />
                      </div>
                    </FormControl>
                    <FormDescription>Choose a date that works best for you.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="message" render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel>Additional Details (Optional)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 size-4 text-muted-foreground" />
                        <textarea className="flex min-h-[120px] w-full rounded-none border-0 border-b border-accent/40 bg-transparent px-3 py-2 pl-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/10 resize-y transition-all" placeholder="Any specific goals or questions for the demo?" {...field} onChange={e => {
                    field.onChange(e);
                    setProgress(100);
                  }} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <div className="mt-0.5 text-destructive"></div>
              <p className="text-sm text-destructive-foreground">{submitError}</p>
            </div>}

          <div className="pt-6 border-t border-border/50 flex justify-end">
            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] group rounded-none bg-linear-to-r from-primary to-chart-4 border-none hover:opacity-90 transition-all font-bold uppercase tracking-wider">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-5" />
                  Processing...
                </> : <>
                  Submit Request
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </>}
            </Button>
          </div>
        </Form>
      </div>
    </div>;
}